import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, Search, Package, Filter, Trash2 } from 'lucide-react'
import api from '../../services/api'
import { todayInManaus, formatDateBR } from '../../utils/dateManaus'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import DateFilterToggle from '../../components/ui/DateFilterToggle'
import ConfirmDialog from '../../components/ConfirmDialog'
import toast from 'react-hot-toast'

// Busca entregas
const fetchDeliveries = async (filters, allDates) => {
  const { scheduledDate, ...rest } = filters
  const params = allDates ? rest : { ...rest, date: scheduledDate }
  const response = await api.get('/api/deliveries', { params })
  return response.data.data
}

// Status badge
function StatusBadge({ status }) {
  const statusConfig = {
    aguardando_saida: { label: 'Aguardando', color: 'bg-gray-100 text-gray-700' },
    em_rota: { label: 'Em rota', color: 'bg-blue-100 text-blue-700' },
    entregue: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
    nao_entregue: { label: 'Não entregue', color: 'bg-red-100 text-red-700' },
    cancelada: { label: 'Cancelada', color: 'bg-gray-100 text-gray-500' },
  }
  const config = statusConfig[status] || statusConfig.aguardando_saida
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
      {config.label}
    </span>
  )
}

export default function DeliveriesList() {
  const [filters, setFilters] = useState({
    scheduledDate: todayInManaus(),
  })
  const [allDates, setAllDates] = useState(false)
  const [dialogState, setDialogState] = useState(null)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['deliveries', filters, allDates],
    queryFn: () => fetchDeliveries(filters, allDates),
  })

  const handleDeleteClick = (id, recipientName) => {
    setDialogState({ id, recipientName })
  }

  const handleConfirmDelete = async () => {
    const { id } = dialogState
    setDialogState(null)

    try {
      await api.delete(`/api/deliveries/${id}`)
      toast.success('Entrega cancelada')
      refetch()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao cancelar entrega')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Entregas</h1>
          <p className="page-subtitle">Gerencie suas entregas e acompanhe o status.</p>
        </div>
        <Link to="/deliveries/new">
          <Button>
            <Plus className="w-4 h-4" />
            Nova Entrega
          </Button>
        </Link>
      </div>

      {/* Filtros */}
      <Card padding="sm">
        <div className="flex flex-wrap items-center gap-4">
          <DateFilterToggle
            value={filters.scheduledDate}
            onChange={(scheduledDate) => setFilters({ ...filters, scheduledDate })}
            allDates={allDates}
            onToggleAllDates={setAllDates}
          />
          <select
            value={filters.status || ''}
            onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
            className="border-0 focus:ring-0 text-gray-600"
          >
            <option value="">Todos os status</option>
            <option value="aguardando_saida">Aguardando</option>
            <option value="em_rota">Em rota</option>
            <option value="entregue">Entregue</option>
            <option value="nao_entregue">Não entregue</option>
          </select>
        </div>
      </Card>

      {/* Lista */}
      <Card padding="none">
        {isLoading ? (
          <div className="p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : data?.deliveries?.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">Nenhuma entrega encontrada</p>
            <Link to="/deliveries/new">
              <Button variant="outline" size="sm">
                <Plus className="w-4 h-4" />
                Criar primeira entrega
              </Button>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {data?.deliveries?.map((delivery) => (
              <div 
                key={delivery.id}
                className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <Package className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {delivery.recipient?.name || 'Destinatário'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {delivery.recipient?.address?.street || delivery.recipient?.addressStreet}, {delivery.recipient?.address?.number || delivery.recipient?.addressNumber} - {delivery.recipient?.address?.neighborhood || delivery.recipient?.addressNeighborhood}
                    </p>
                    {delivery.notes && (
                      <p className="text-xs text-gray-400 mt-1">
                        📝 {delivery.notes}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Agendada: {formatDateBR(delivery.scheduledDate)} · Criada em: {new Date(delivery.createdAt).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm text-gray-500">
                      {delivery.trackingCode}
                    </p>
                  </div>
                  <StatusBadge status={delivery.status} />
                  {delivery.status === 'aguardando_saida' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteClick(delivery.id, delivery.recipient?.name)
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Cancelar entrega"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!dialogState}
        title="Cancelar entrega"
        message={dialogState ? `Cancelar entrega para "${dialogState.recipientName}"?` : ''}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDialogState(null)}
      />
    </div>
  )
}
